import { useMemo } from 'react';
import './KnowledgeWordsBackground.css';

const KNOWLEDGE_WORDS = [
    'Philosophie', 'Histoire', 'Astronomie', 'Littérature', 'Sciences',
    'Art', 'Musique', 'Géographie', 'Archéologie', 'Poésie',
    'Mathématiques', 'Physique', 'Chimie', 'Biologie', 'Sociologie',
    'Psychologie', 'Économie', 'Architecture', 'Théâtre', 'Cinéma',
    'Photographie', 'Sculpture', 'Peinture', 'Danse', 'Opéra',
    'Botanique', 'Zoologie', 'Géologie', 'Climatologie', 'Écologie',
    'Anthropologie', 'Ethnologie', 'Linguistique', 'Mythologie', 'Épigraphie',
    'Paléontologie', 'Numismatique', 'Héraldique', 'Généalogie', 'Cartographie',
    'Cosmologie', 'Informatique', 'Robotique', 'Gastronomie', 'Œnologie',
    'Philosophie', 'Médecine', 'Droit', 'Politique', 'Diplomatie',
    'Stratégie', 'Navigation', 'Exploration', 'Invention', 'Découverte',
    'Renaissance', 'Baroque', 'Romantisme', 'Impressionnisme', 'Surréalisme',
    'Humanisme', 'Lumières', 'Modernité', 'Sagesse', 'Culture',
    'Savoir', 'Connaissance', 'Éducation', 'Apprentissage', 'Curiosité',
    'Réflexion', 'Imagination', 'Créativité', 'Innovation', 'Tradition',
    'Patrimoine', 'Mémoire', 'Héritage', 'Transmission', 'Partage',
    'Dialogue', 'Échange', 'Rencontre', 'Ouverture', 'Tolérance',
    'Liberté', 'Fraternité', 'Solidarité', 'Engagement', 'Citoyenneté',
    'Démocratie', 'Géopolitique', 'Justice', 'Éthique', 'Vertu',
    'Astronomie', 'Océanographie', 'Volcanologie', 'Minéralogie', 'Cristallographie',
    // Nouveaux mots liés aux conférences
    'Actualité', 'Monde', 'Europe', 'International', 'Société',
    'Santé', 'Bien-être', 'Médecine', 'Nutrition', 'Psychologie',
    'Environnement', 'Développement', 'Durable', 'Climat', 'Énergie',
    'Technologies', 'Futur', 'Intelligence', 'Artificielle', 'Numérique',
    'Voyage', 'Exploration', 'Aventure', 'Découverte', 'Horizons',
    'Nature', 'Jardins', 'Faune', 'Flore', 'Biodiversité',
    'Cinéma', 'Théâtre', 'Concerts', 'Expositions', 'Musées',
    'Littérature', 'Écriture', 'Lecture', 'Auteurs', 'Livres',
    'Patrimoine', 'Monuments', 'Architecture', 'Urbanisme', 'Ville',
    'Ruralité', 'Terroir', 'Traditions', 'Folklore', 'Coutumes',
];

// Configuration for each row: slower seeds & gentle angles
const ROW_CONFIGS = [
    // Removed Top Horizontal Row to clear header (was at 5%)

    // Gentle Diagonals - Down/Right Crosses (\ direction) - 12deg - Moved up slightly
    { speed: 150, direction: 'diagonal-up', fontSize: 'text-5xl', opacity: 'opacity-20', top: '15%', rotate: 'rotate-gentle' },

    // Gentle Diagonals - Up/Right Crosses (/ direction) - 12deg
    { speed: 180, direction: 'diagonal-up', fontSize: 'text-4xl', opacity: 'opacity-20', top: '35%', rotate: 'rotate-gentle-minus' },

    // Center chaotic mix - Slower
    { speed: 130, direction: 'left', fontSize: 'text-6xl', opacity: 'opacity-10', top: '48%' },
    { speed: 110, direction: 'right', fontSize: 'text-lg', opacity: 'opacity-35', top: '55%' },

    // Gentle Diagonals - Up/Right Crosses (/ direction) - 12deg - Lower section
    { speed: 200, direction: 'diagonal-down', fontSize: 'text-xl', opacity: 'opacity-25', top: '65%', rotate: 'rotate-gentle-minus' },

    // Gentle Diagonals - Down/Right Crosses (\ direction) - 12deg - Lower section
    { speed: 190, direction: 'diagonal-down', fontSize: 'text-2xl', opacity: 'opacity-30', top: '75%', rotate: 'rotate-gentle' },

    // Horizonals - Bottom
    { speed: 150, direction: 'right', fontSize: 'text-3xl', opacity: 'opacity-30', top: '88%' },
];

function shuffleArray(arr) {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

export default function KnowledgeWordsBackground() {
    const rows = useMemo(() => {
        return ROW_CONFIGS.map((config, index) => {
            const words = shuffleArray(KNOWLEDGE_WORDS);
            const selectedWords = words.slice(0, 35);
            const displayWords = [...selectedWords, ...selectedWords];
            return { ...config, words: displayWords, id: index };
        });
    }, []);

    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none select-none bg-primary-950">
            {/* White nuance overlay */}
            <div className="absolute inset-0 bg-white/5 z-0" />

            {/* Existing gradient overlay for depth */}
            <div className="absolute inset-0 bg-gradient-to-b from-primary-950/80 via-transparent to-primary-950/90 z-20" />

            {rows.map((row) => (
                <div
                    key={row.id}
                    className={`absolute whitespace-nowrap z-10 ${row.rotate ? `diagonal-wrapper ${row.rotate}` : 'w-full'}`}
                    style={{ top: row.top }}
                >
                    <div
                        className={`knowledge-scroll knowledge-scroll--${row.direction} ${row.fontSize} ${row.opacity} text-white font-serif tracking-widest font-bold uppercase mix-blend-overlay`}
                        style={{ '--scroll-duration': `${row.speed}s` }}
                    >
                        {row.words.map((word, i) => (
                            <span key={i} className="inline-block mx-8 md:mx-12 drop-shadow-sm">
                                {word}
                            </span>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
